import { useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useCancelMentorshipRequest } from "@/hooks/useMentorshipRequests";

interface CancelMentorshipRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requestId: string;
  mentorName?: string;
  isAdmin?: boolean;
}

export function CancelMentorshipRequestDialog({
  open,
  onOpenChange,
  requestId,
  mentorName,
  isAdmin = false,
}: CancelMentorshipRequestDialogProps) {
  const [reason, setReason] = useState("");
  const cancelRequest = useCancelMentorshipRequest();

  const handleConfirmCancel = async () => {
    try {
      await cancelRequest.mutateAsync({
        requestId,
        reason: reason.trim() || undefined,
      });
      onOpenChange(false);
      setReason("");
    } catch (error) {
      // Error handled in mutation
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setReason("");
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel mentorship request?</AlertDialogTitle>
          <AlertDialogDescription>
            {isAdmin
              ? `This will cancel the mentorship request and notify both the entrepreneur and mentor.`
              : `This will cancel your mentorship request${mentorName ? ` to ${mentorName}` : ""} and notify the mentor.`}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="cancel-reason" className="text-sm text-muted-foreground">
            {isAdmin ? "Reason (optional, will be shown to entrepreneur)" : "Add a note (optional)"}
          </Label>
          <Textarea
            id="cancel-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={isAdmin ? "Enter reason for cancellation..." : "Optional: add a note"}
            rows={3}
          />
        </div>

        <AlertDialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={cancelRequest.isPending}>
            Keep Request
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmCancel}
            disabled={cancelRequest.isPending}
          >
            {cancelRequest.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Cancel Request
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
